// カフェメニュー（ドリンク）のカタログと純ロジック。M5 の追加システム。
//
// GAME_DESIGN.md §5: メニュー開発（チップで新ドリンクを解放）。各ドリンクは
// 誘引属性を 1 つ持ち、「本日のスペシャル」に選ぶとその属性が庭属性に加算される。
// あわせて在店客のチップに小さなボーナスが乗る。
//
// 設計のねらい:
//  - スペシャルの属性は庭デコだけでは届きにくい複合条件の「あと一押し」になる配分にする
//    （例: 結晶獣プリズムの結晶≥5・水辺≥1、樹守ねむの緑≥4 等にメニューで寄与できる）。
//  - この層は描画・DOM・乱数に触れない純データ＋純関数。状態は GameState.menu が持つ。

import type { AttributeKey } from './decos';

/** ドリンク 1 種の定義。 */
export interface DrinkDef {
  /** 安定 ID（セーブに焼かれるので将来も変えない）。 */
  id: string;
  /** 表示名（日本語）。 */
  name: string;
  /** UI のドット絵的アイコン（絵文字。メニューはカップの中身で識別）。 */
  icon: string;
  /** 開発コスト（チップ）。一度開発すれば永続。 */
  cost: number;
  /** このドリンクが持つ誘引属性（1 つ）。スペシャル選択中に庭属性へ加算される。 */
  attribute: AttributeKey;
  /** スペシャル選択中に庭属性へ加える量。 */
  attributeAmount: number;
  /** スペシャル選択中、来店客 1 組ごとのチップに乗る小さなボーナス。 */
  tipBonus: number;
  /** フレーバー（メニューの説明文）。 */
  flavor: string;
}

/**
 * ドリンクカタログ（6 種）。属性は 7 種から重複しすぎないよう散らす。
 * 安いものから高いものへ並べる（UI の表示順）。複合条件のレア客を後押しできるよう
 * crystal / water / green / calm / light / scent をひととおりカバーする。
 */
export const DRINK_CATALOG: readonly DrinkDef[] = [
  {
    id: 'comet_latte',
    name: 'コメットラテ',
    icon: '☕',
    cost: 120,
    attribute: 'scent',
    attributeAmount: 2,
    tipBonus: 1,
    flavor: 'ほうき星のかたちに泡を描いた一杯。香りで常連を呼びやすくなる。',
  },
  {
    id: 'mint_soda',
    name: 'ミントソーダ',
    icon: '🥤',
    cost: 180,
    attribute: 'water',
    attributeAmount: 1,
    tipBonus: 1,
    flavor: 'しゅわっと弾ける水色のソーダ。水辺の気配がほんのり増す。',
  },
  {
    id: 'moonlit_tea',
    name: '月見ハーブティー',
    icon: '🍵',
    cost: 240,
    attribute: 'calm',
    attributeAmount: 2,
    tipBonus: 2,
    flavor: '夜にだけ香るハーブの茶。静けさが深まり、夢見の客が来やすくなる。',
  },
  {
    id: 'starlight_cocoa',
    name: '星あかりココア',
    icon: '🍫',
    cost: 320,
    attribute: 'light',
    attributeAmount: 2,
    tipBonus: 2,
    flavor: 'ふわりと灯るマシュマロを浮かべたココア。光に集まる客の後押しに。',
  },
  {
    id: 'crystal_ade',
    name: 'クリスタルエード',
    icon: '🧊',
    cost: 480,
    attribute: 'crystal',
    attributeAmount: 2,
    tipBonus: 3,
    flavor: '氷の結晶がきらめく一杯。結晶づくしの庭の「あと一押し」になる。',
  },
  {
    id: 'mossy_smoothie',
    name: 'こけ生いスムージー',
    icon: '🥗',
    cost: 400,
    attribute: 'green',
    attributeAmount: 2,
    tipBonus: 3,
    flavor: '若葉をすりおろした緑のスムージー。緑ゆたかな庭の象徴客を後押し。',
  },
];

/** ID → ドリンク定義の索引。未知 ID は undefined。 */
const DRINK_BY_ID: ReadonlyMap<string, DrinkDef> = new Map(
  DRINK_CATALOG.map((d) => [d.id, d]),
);

/** ID からドリンク定義を引く。未知 ID なら undefined。 */
export function getDrink(id: string): DrinkDef | undefined {
  return DRINK_BY_ID.get(id);
}

/**
 * カフェメニューの状態（GameState.menu）。
 *  - developed: 開発済みドリンク ID の集合（順序は不問だが配列で保持）。
 *  - special: 現在「本日のスペシャル」に選んでいるドリンク ID（未選択は null）。
 *    special は developed に含まれていなければならない（不変条件）。
 */
export interface MenuState {
  developed: string[];
  special: string | null;
}

/** 新規ゲームのメニュー初期状態（何も開発しておらず、スペシャル未選択）。 */
export function createInitialMenu(): MenuState {
  return { developed: [], special: null };
}

/** そのドリンクが開発済みか。 */
export function isDeveloped(menu: MenuState, drinkId: string): boolean {
  return menu.developed.includes(drinkId);
}

/**
 * ドリンクを開発する純関数。検証に通らなければ状態をそのまま返す（拒否）。
 *
 * 拒否条件: 未知の ID / 既に開発済み / チップ不足。
 * 成功時はチップを開発コストぶん減らし、developed に追加した新しい GameState を返す。
 * 開発しただけではスペシャルにはしない（選択は別操作）。
 *
 * MenuState だけでなくチップ残高も触るので、GameState を受けて GameState を返す。
 */
export function developDrink<S extends { chips: number; menu: MenuState }>(
  state: S,
  drinkId: string,
): S {
  const drink = getDrink(drinkId);
  if (!drink) return state;
  if (isDeveloped(state.menu, drinkId)) return state;
  if (state.chips < drink.cost) return state;
  return {
    ...state,
    chips: state.chips - drink.cost,
    menu: { ...state.menu, developed: [...state.menu.developed, drinkId] },
  };
}

/**
 * 「本日のスペシャル」を選ぶ純関数。開発済みのドリンクのみ選べる。
 * 既に同じドリンクが選ばれていれば解除する（トグル）。未開発・未知は拒否。
 */
export function setSpecial<S extends { menu: MenuState }>(state: S, drinkId: string | null): S {
  if (drinkId == null) {
    if (state.menu.special == null) return state;
    return { ...state, menu: { ...state.menu, special: null } };
  }
  if (!isDeveloped(state.menu, drinkId)) return state; // 未開発は選べない
  const next = state.menu.special === drinkId ? null : drinkId; // 再選択で解除
  if (next === state.menu.special) return state;
  return { ...state, menu: { ...state.menu, special: next } };
}

/** 現在のスペシャルが庭属性へ加える寄与を返す（未選択なら寄与なし）。 */
export function specialAttribute(menu: MenuState): { key: AttributeKey; amount: number } | null {
  if (menu.special == null) return null;
  const drink = getDrink(menu.special);
  if (!drink) return null;
  return { key: drink.attribute, amount: drink.attributeAmount };
}

/** 現在のスペシャルが来店 1 組ごとに乗せるチップボーナス（未選択は 0）。 */
export function specialTipBonus(menu: MenuState): number {
  if (menu.special == null) return 0;
  const drink = getDrink(menu.special);
  return drink ? drink.tipBonus : 0;
}

// =========================================================================
// スペシャルのチップ乗算（v0.8.1）。メニューを「飾り」から「生きた収入レバー」へ。
// =========================================================================
//
// スペシャル未設定だと収入が目に見えて落ちる＝メニューを使うのが前提になるよう、
// スペシャルにチップ乗算を持たせる:
//  - 設定しているだけで全来店に小さな全体ボーナス（SPECIAL_GLOBAL_TIP）。
//  - スペシャルの属性が「その客のキー属性」に一致すると大きく乗る（SPECIAL_MATCH_TIP）。
//    ＝自分の focus 客に合うドリンクを選ぶ生きた選択になる。
//  - さらにその客の季節を「季節別館（拡張島）」が持っていれば最大化（SPECIAL_THEMED_TIP）。
//    ＝島テーマ × メニューを揃えた「テーマ一致」の重ねがけ報酬。

/** スペシャル設定中、属性不一致の客に乗る全体チップ倍率。 */
export const SPECIAL_GLOBAL_TIP = 1.12;
/** スペシャルの属性がその客のキー属性に一致したときのチップ倍率。 */
export const SPECIAL_MATCH_TIP = 1.5;
/** 一致 ＋ その客の季節別館もアンロック済み（テーマ一致）のときのチップ倍率。 */
export const SPECIAL_THEMED_TIP = 2.0;

/**
 * ある客 1 組のチップに乗るスペシャル由来の倍率（v0.8.1）。
 *
 * @param menu         メニュー状態（スペシャル選択）。
 * @param keyAttr      その客のキー属性（customers の keyAttribute）。
 * @param seasonHosted その客の好む季節を季節別館がアンロック済みで持っているか（テーマ一致）。
 */
export function specialTipMultiplier(
  menu: MenuState,
  keyAttr: string,
  seasonHosted: boolean,
): number {
  const drink = menu.special ? getDrink(menu.special) : null;
  if (!drink) return 1.0; // スペシャル未設定＝ボーナスなし（設定するのが前提）
  if (drink.attribute === keyAttr) return seasonHosted ? SPECIAL_THEMED_TIP : SPECIAL_MATCH_TIP;
  return SPECIAL_GLOBAL_TIP;
}
